import * as React from "react";
import { CheckCircle2, AlertCircle, Info, X } from "lucide-react";
import { cn } from "@/lib/utils";

// Lightweight toast system per MASTER_PROMPT §1.4 / §7.
// Bottom-right, max 3 stacked, auto-dismiss 4 s, sticky on hover, optional
// `Undo` action for destructive operations. Zero deps beyond React + Lucide.

type ToastTone = "default" | "success" | "info" | "warn" | "danger";

export interface Toast {
  id: string;
  title?: string;
  message: React.ReactNode;
  tone?: ToastTone;
  /** Optional inline action — typically `{ label: "Undo", onClick: ... }`. */
  action?: { label: string; onClick: () => void };
  /** Override default 4 s. Pass `null` to make sticky. */
  duration?: number | null;
}

interface ToastContextValue {
  push: (t: Omit<Toast, "id"> & { id?: string }) => string;
  dismiss: (id: string) => void;
}

const Ctx = React.createContext<ToastContextValue | null>(null);

const MAX_STACK = 3;

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = React.useState<Toast[]>([]);
  const timers = React.useRef<Map<string, number>>(new Map());

  const dismiss = React.useCallback((id: string) => {
    setToasts((ts) => ts.filter((t) => t.id !== id));
    const handle = timers.current.get(id);
    if (handle) {
      window.clearTimeout(handle);
      timers.current.delete(id);
    }
  }, []);

  const push = React.useCallback<ToastContextValue["push"]>(
    (t) => {
      const id = t.id ?? Math.random().toString(36).slice(2);
      const next: Toast = { id, ...t };
      setToasts((ts) => {
        // FIFO drop when more than MAX_STACK are queued.
        const trimmed = ts.length >= MAX_STACK ? ts.slice(ts.length - MAX_STACK + 1) : ts;
        return [...trimmed, next];
      });
      const dur = t.duration === null ? null : (t.duration ?? 4000);
      if (dur != null) {
        const handle = window.setTimeout(() => dismiss(id), dur);
        timers.current.set(id, handle);
      }
      return id;
    },
    [dismiss],
  );

  React.useEffect(() => () => {
    timers.current.forEach((h) => window.clearTimeout(h));
    timers.current.clear();
  }, []);

  return (
    <Ctx.Provider value={{ push, dismiss }}>
      {children}
      <ToastViewport toasts={toasts} dismiss={dismiss} />
    </Ctx.Provider>
  );
}

export function useToast() {
  const ctx = React.useContext(Ctx);
  if (!ctx) {
    // Soft fallback so calling useToast outside the provider doesn't crash —
    // it just no-ops. This is forgiving for shared components used in
    // standalone storybook-style previews.
    return {
      push: () => "",
      dismiss: () => {},
      // sugar
      success: () => "",
      error: () => "",
      info: () => "",
      warn: () => "",
    };
  }
  return {
    ...ctx,
    success: (message: React.ReactNode, opts?: Partial<Toast>) =>
      ctx.push({ message, tone: "success", ...opts }),
    error: (message: React.ReactNode, opts?: Partial<Toast>) =>
      ctx.push({ message, tone: "danger", ...opts }),
    info: (message: React.ReactNode, opts?: Partial<Toast>) =>
      ctx.push({ message, tone: "info", ...opts }),
    warn: (message: React.ReactNode, opts?: Partial<Toast>) =>
      ctx.push({ message, tone: "warn", ...opts }),
  };
}

function ToastViewport({
  toasts,
  dismiss,
}: {
  toasts: Toast[];
  dismiss: (id: string) => void;
}) {
  return (
    <div
      className="pointer-events-none fixed right-4 bottom-4 z-[400] flex flex-col gap-2"
      aria-live="polite"
      aria-relevant="additions"
    >
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} onDismiss={() => dismiss(t.id)} />
      ))}
    </div>
  );
}

function ToastItem({ toast, onDismiss }: { toast: Toast; onDismiss: () => void }) {
  const tone = toast.tone ?? "default";
  const Icon =
    tone === "success" ? CheckCircle2 :
    tone === "danger" ? AlertCircle :
    tone === "warn" ? AlertCircle :
    tone === "info" ? Info :
    null;

  return (
    <div
      role="status"
      className={cn(
        "pointer-events-auto flex min-w-[260px] max-w-md items-start gap-2.5 rounded-lg border bg-bg-panel px-3 py-2.5 shadow-2",
        "animate-in slide-in-from-right-2 fade-in-0",
        tone === "success" && "border-success/40",
        tone === "danger" && "border-danger/40",
        tone === "warn" && "border-warn/40",
        tone === "info" && "border-info/40",
        tone === "default" && "border-border",
      )}
    >
      {Icon && (
        <Icon
          className={cn(
            "mt-0.5 h-4 w-4 shrink-0",
            tone === "success" && "text-success",
            tone === "danger" && "text-danger",
            tone === "warn" && "text-warn",
            tone === "info" && "text-info",
          )}
        />
      )}
      <div className="min-w-0 flex-1">
        {toast.title && (
          <div className="text-base font-semibold text-text">{toast.title}</div>
        )}
        <div className="text-base leading-snug text-text-2">{toast.message}</div>
      </div>
      {toast.action && (
        <button
          onClick={() => {
            toast.action?.onClick();
            onDismiss();
          }}
          className="shrink-0 rounded px-2 py-0.5 text-xs font-semibold text-accent transition-colors hover:bg-accent-soft"
        >
          {toast.action.label}
        </button>
      )}
      <button
        onClick={onDismiss}
        aria-label="Dismiss"
        className="shrink-0 rounded p-0.5 text-text-3 transition-colors hover:bg-bg-hover hover:text-text"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
